import { useQuery } from '@tanstack/vue-query'
import { type Ref, computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import { useUrl } from '@/composables/url-helpers'
import { useI18nAddon } from '@/i18n/i18n'
import { usePersisterFn } from '@/query/persister-plugin'
import { Persisters } from '@/query/shared'
import { ROUTE_NAMES } from '@/router/constants'
import { SSR_QUERY } from '@/router/query-params'
import { useAppStore } from '@/store/app-store'
import {
    type APICategoryShortInfo,
    type APIGameType,
    type APIRedirect,
    type CategoryShortInfo,
    type GameCategoriesInfoType,
    type GameLeaderboard,
    type LinkedCategory,
    RedirectMechanism,
    RedirectReason,
} from '@/types'
import { unzipShortGame, zipAPIGame } from '@/utils/helpers'
import { useAPI } from '@/utils/requests/api-plugin'
import { isCountryRestricted } from '@/utils/requests/utils'

import {
    GAME_PAGE_RECOMMENDATION_TYPES,
    type GamePageRecommendations,
    type GamePageRecommendationsZip,
    emptyGamePageRecommendations,
    unzipGamePageRecommendations,
    zipGamePageRecommendations,
} from './game-recommendations'

type UseGamePageDataParams = {
    hru: Ref<string>
    isExport: boolean
}

export const useGamePageData = ({ hru, isExport }: UseGamePageDataParams) => {
    const appStore = useAppStore()
    const route = useRoute()
    const router = useRouter()
    const { getCategoryRoute } = useUrl()
    const { currentLocale } = useI18nAddon()
    const { getGameByParam } = useAPI()

    const showHidden = computed(() => route.query[SSR_QUERY.SHOW_HIDDEN] === 'true')
    const gameQueryKey = computed(() => [
        'game-data',
        hru.value,
        currentLocale.value,
        isExport,
        showHidden.value,
        appStore.clientType,
    ])
    const enabled = computed(() => !!hru.value)

    const {
        data: gameData,
        suspense: gameSuspense,
        isError: gameHasError,
    } = useQuery({
        queryKey: gameQueryKey,
        enabled,
        queryFn: async () => {
            const result = await getGameByParam({
                hru: hru.value,
                locale: currentLocale.value,
                context: isExport ? 'export' : 'portal',
                showHidden: showHidden.value,
                clientType: appStore.clientType,
            })

            if (result.status === 'error') {
                throw new Error(result.message)
            }

            if (result.status !== 'ok') {
                return result
            }

            const convertCategory = (category: APICategoryShortInfo): CategoryShortInfo => ({
                ...category,
                href: getCategoryRoute({ hru: category.hru, type: category.categoryType }),
            })

            const leaderboard = {
                entries: result.data.leaderboard?.entries?.slice(0, 10) || [],
                totalPlayers: result.data.leaderboard?.total_players ?? 0,
            }

            return {
                status: 'ok' as const,
                data: {
                    game: result.data.game,
                    gameCategoriesInfo: {
                        pathCategories: result.data.pathCategories.map(convertCategory),
                        categoriesInfo: result.data.categoriesInfo.map(convertCategory),
                        collectionsInfo: (result.data.collectionsInfo ?? []).map(convertCategory),
                    },
                    leaderboard,
                },
            }
        },
        persister: usePersisterFn(Persisters.HighCardinality),
    })

    const game = computed<APIGameType | undefined>(() =>
        gameData.value?.status === 'ok' ? gameData.value.data.game : undefined,
    )
    const gameRedirect = computed<APIRedirect | null>(() => {
        if (game.value && isCountryRestricted(game.value.restricted_countries, appStore.countryCode)) {
            return {
                redirectTarget: {
                    type: 'main-page',
                    mechanism: RedirectMechanism.FRONTEND_REDIRECT,
                    reason: RedirectReason.COUNTRY_RESTRICTED,
                },
                status: 302,
            }
        }
        return gameData.value?.status === 'redirect' ? gameData.value.redirect : null
    })
    const gameCategoriesInfo = computed<GameCategoriesInfoType | undefined>(() =>
        gameData.value?.status === 'ok' ? gameData.value.data.gameCategoriesInfo : undefined,
    )
    const leaderboard = computed<GameLeaderboard>(() =>
        gameData.value?.status === 'ok' ? gameData.value.data.leaderboard : { entries: [], totalPlayers: 0 },
    )

    function processGameRedirectResponse() {
        if (!gameRedirect.value) {
            return
        }
        const { redirectTarget, status: statusCode } = gameRedirect.value

        let redirectURL = ''

        if (redirectTarget.type === 'game') {
            redirectURL = router.resolve({
                name: isExport ? ROUTE_NAMES.EXPORT_GAME_PAGE : ROUTE_NAMES.GAME_PAGE,
                params: { game: redirectTarget.game_hru },
                query: route.query,
            }).href
        } else if (redirectTarget.type === 'category') {
            redirectURL = router.resolve({
                ...getCategoryRoute({ hru: redirectTarget.cat_hru, type: redirectTarget.cat_type }),
                query: route.query,
            }).href
        } else if (redirectTarget.type === 'main-page') {
            redirectURL = router.resolve({
                name: ROUTE_NAMES.MAIN,
                query: route.query,
            }).href
        }

        appStore.setHeaders({
            'X-BFF-Redirect-Mechanism': redirectTarget.mechanism,
            'X-BFF-Redirect-Reason': redirectTarget.reason,
        })
        appStore.setPageStatus(statusCode)
        appStore.setTargetURL(redirectURL)
    }

    function processGameResponse() {
        if (gameRedirect.value) {
            processGameRedirectResponse()
            return false
        }
        if (!game.value) {
            if (gameHasError.value) {
                appStore.setPageStatus(500)
            } else if (gameData.value?.status === 'not-found') {
                appStore.setPageStatus(gameData.value.httpStatus)
            } else {
                appStore.setPageStatus(404)
            }
            return false
        }
        return true
    }

    return {
        game,
        gameRedirect,
        gameCategoriesInfo,
        leaderboard,
        preloadGameData: gameSuspense,
        processGameResponse,
    }
}

export const useGameRecomendations = ({ hru }: { hru: Ref<string> }) => {
    const appStore = useAppStore()
    const { currentLocale } = useI18nAddon()
    const { getGameRecommendations } = useAPI()
    const recommendationExperiment = computed(() => {
        return appStore.experiments['new-main-category']?.sample === 'main-category' ? 'main-category' : undefined
    })
    const recommendationsQueryKey = computed(() => [
        'game-recommendations',
        hru.value,
        currentLocale.value,
        appStore.platform,
        appStore.contentScope,
        appStore.rankingParams?.clusterId,
        appStore.rankingParams?.audience,
        recommendationExperiment.value,
    ])

    const { data: recommendationsData, suspense: recommendationsSuspense } = useQuery({
        queryKey: recommendationsQueryKey,
        queryFn: async () => {
            const result = await getGameRecommendations({
                hru: hru.value,
                types: GAME_PAGE_RECOMMENDATION_TYPES,
                platform: appStore.platform,
                scope: appStore.contentScope,
                rankingClusterId: appStore.rankingParams?.clusterId,
                rankingAudience: appStore.rankingParams?.audience,
                experiment: recommendationExperiment.value,
                locale: currentLocale.value,
            })

            if (result.status !== 'ok') {
                return {
                    recommendationsRaw: emptyGamePageRecommendations(),
                    linkedCategories: undefined,
                }
            }

            return {
                recommendationsRaw: zipGamePageRecommendations(result.data),
                linkedCategories: result.data.linked_categories?.map((category) => ({
                    hru: category.localizations[0]?.hru,
                    categoryType: category.category_type,
                    title: category.localizations[0]?.title,
                })),
            }
        },
        persister: usePersisterFn(Persisters.HighCardinality),
    })

    const recommendationsRaw = computed<GamePageRecommendationsZip | undefined>(
        () => recommendationsData.value?.recommendationsRaw,
    )

    const linkedCategories = computed<LinkedCategory[] | undefined>(() => recommendationsData.value?.linkedCategories)

    const personalizedRecommendationsQueryKey = computed(() => [
        'personalized-game-recommendations',
        hru.value,
        currentLocale.value,
        appStore.platform,
        appStore.contentScope,
        appStore.clid,
        appStore.rankingParams?.clusterId,
        appStore.rankingParams?.audience,
        appStore.uacParams,
    ])
    const { data: personalizedRecommendationsData, suspense: personalizedRecommendationsSuspense } = useQuery({
        queryKey: personalizedRecommendationsQueryKey,
        queryFn: async () => {
            const result = await getGameRecommendations({
                hru: hru.value,
                types: ['recommended', 'related'],
                platform: appStore.platform,
                scope: appStore.contentScope,
                rankingClusterId: appStore.rankingParams?.clusterId,
                rankingAudience: appStore.rankingParams?.audience,
                clid: appStore.clid,
                uac: appStore.uacParams,
                locale: currentLocale.value,
                personalized: true,
            })

            return result.status === 'ok'
                ? {
                      recommended: result.data.recommended?.slice(0, 12).map(zipAPIGame) || [],
                      related: result.data.related?.slice(0, 12).map(zipAPIGame) || [],
                      slateId: result.data.slate_id,
                  }
                : { recommended: [], related: [], slateId: undefined }
        },
        // Personalized responses are user-specific and must never enter the SSR persister cache.
        persister: undefined,
    })

    const recommendations = computed<GamePageRecommendations | undefined>(() => {
        if (!recommendationsRaw.value) {
            return undefined
        }
        return {
            ...unzipGamePageRecommendations(recommendationsRaw.value),
            recommended: personalizedRecommendationsData.value?.recommended.map(unzipShortGame) || [],
            related: personalizedRecommendationsData.value?.related.length
                ? personalizedRecommendationsData.value?.related.map(unzipShortGame)
                : recommendationsRaw.value.related.map(unzipShortGame),
        }
    })

    return {
        recommendationsRaw,
        recommendations,
        recommendationSlateId: computed(() => personalizedRecommendationsData.value?.slateId),
        linkedCategories,
        preloadRecommendations: () => Promise.all([recommendationsSuspense(), personalizedRecommendationsSuspense()]),
    }
}
